// PHA-790: plain-language SMS subscription (consent) wording for the care team.
//
// A patient who texts STOP is unsubscribed by Twilio/carrier rules (TCPA). Nobody on our side can
// re-subscribe them - only the patient texting START can. So the UI shows the state and the
// next step (call, or switch to phone outreach), and deliberately offers no re-subscribe action.

export const SMS_OPT_OUT_NEXT_STEP =
  'They must text START to resume, or use phone outreach.';

export const SMS_OPT_OUT_FAILURE_MESSAGE = `Patient opted out of texts (replied STOP). ${SMS_OPT_OUT_NEXT_STEP}`;

export interface SmsSubscriptionFields {
  sms_subscription_status?: 'subscribed' | 'unsubscribed' | null;
  sms_status_changed_at?: string | null;
  /** 'twilio_inbound_stop' / 'ringcentral_inbound_stop' (PHA-1202) = date the patient texted STOP;
   *  'twilio_send_exception' = date a send first bounced. */
  sms_status_source?: string | null;
}

/** True when a failed-send error is Twilio's "recipient has replied STOP" refusal (21610). */
export const isSmsUnsubscribedError = (error?: string | null): boolean => {
  if (!error) return false;
  const e = error.toLowerCase();
  return e.includes('unsubscribed recipient') || e.includes('21610');
};

/** The "Survey Not Sent" reason: friendly text for the STOP case, the raw error otherwise. */
export const surveyFailureReason = (error: string): string =>
  isSmsUnsubscribedError(error) ? SMS_OPT_OUT_FAILURE_MESSAGE : error;

export const formatSmsStatusDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

/** Chip label, e.g. "SMS: Unsubscribed (since 10/3/2026)". */
export const smsSubscriptionLabel = (s: SmsSubscriptionFields): string | null => {
  if (!s.sms_subscription_status) return null;
  const date = formatSmsStatusDate(s.sms_status_changed_at);
  if (s.sms_subscription_status === 'unsubscribed') {
    return `SMS: Unsubscribed${date ? ` (since ${date})` : ''}`;
  }
  return `SMS: Subscribed${date ? ` (resumed ${date})` : ''}`;
};

/** Tooltip explaining what the date means and what to do. Null when subscribed. */
export const smsSubscriptionDetail = (s: SmsSubscriptionFields): string | null => {
  if (s.sms_subscription_status !== 'unsubscribed') return null;
  const dateMeaning =
    s.sms_status_source === 'twilio_inbound_stop' || s.sms_status_source === 'ringcentral_inbound_stop'
      ? 'The date is when the patient replied STOP.'
      : 'The date is when we first learned of it (a text was refused); the patient may have replied STOP earlier.';
  return `Patient opted out of texts (replied STOP). Survey texts cannot be sent. ${SMS_OPT_OUT_NEXT_STEP} ${dateMeaning}`;
};
